import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionExpression,
  FilterConditionPluginFilterType,
  FilterPluginSpecificationRestriction,
  FilterRegistrationToken,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionAdministrativeDistrictIdPlugin,
  FilterConditionAdmissionCausePlugin,
  FilterConditionAdmissionDatePlugin,
  FilterConditionAdmissionReasonPlugin,
  FilterConditionAgmPlugin,
  FilterConditionArtificialRespirationHoursPlugin,
  FilterConditionChargeRealmPlugin,
  FilterConditionCodeSpectrumPlugin,
  FilterConditionDismissalDatePlugin,
  FilterConditionDismissionCausePlugin,
  FilterConditionDrgCodeExistsPlugin,
  FilterConditionDrivingTimePlugin,
  FilterConditionFabKeysPlugin,
  FilterConditionInlierTypesPlugin,
  FilterConditionInsuranceTypePlugin,
  FilterConditionKhtgLeistungsgruppePlugin,
  FilterConditionNrwServiceAreaIdsPlugin,
  FilterConditionNrwServiceGroupIdsPlugin,
  FilterConditionPluginAbstractFilter,
  FilterConditionPostalCodesPlugin,
  FilterConditionStateIdPlugin,
  FilterConditionUtil,
  FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin,
  FilterConditionVirtualClinicSpecialtyDepartmentIdsPluginType, FilterConditionMarketIdPlugin
} from '@bindoc-projects/filter-condition-domain-lib';
import { FilterConditionAbteilungsartPlugin } from './abteilungsart.plugin';
import { FilterConditionAopPlugin } from './aop.plugin';
import { FilterConditionGenderPlugin } from './gender.plugin';
import { FilterConditionHartmannCodeGroupIdsPlugin } from './hartmann-code-group-id.plugin';
import { FilterConditionIgesPlugin } from './iges.plugin';
import { FilterConditionPcclPlugin } from './pccl.plugin';
import { FilterConditionOperatingNumberFilterPlugin } from './operating-number-filter.plugin';
import { FilterConditionHybridDrgPlugin } from './hybrid-drg.plugin';
import { FilterConditionIntuitiveFileOfTruthCategoryPlugin } from './intuitive-file-of-truth-category.plugin';
import { FilterConditionIntuitiveFileOfTruthProcedureNamePlugin } from './intuitive-file-of-truth-procedure-name.plugin';
import { FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin } from './intuitive-file-of-truth-procedure-subject.plugin';
import { FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin } from './intuitive-file-of-truth-type-of-entry.plugin';
import { FilterConditionIntuitiveFileOfTruthConversionPlugin } from './intuitive-file-of-truth-conversion.plugin';
import { FilterConditionOpNotfalleingriffPlugin } from './op-notfalleingriff.plugin';
import { FilterConditionOpHasDataPlugin } from './op-has-data.plugin';
import { FilterConditionOpDaVinciSystemIdPlugin } from './op-davinci-system-id.plugin';
import { FilterConditionOpSaalPlugin } from './op-saal.plugin';

export class VirtualClinicAnalysisFullyInpatientCasesFilter extends FilterConditionPluginAbstractFilter {
  public static SPECIFICATIONS: FilterPluginSpecificationRestriction = [
    { specification: FilterConditionMarketIdPlugin.Specification },
    { specification: FilterConditionAopPlugin.Specification },
    {
      specification: FilterConditionHartmannCodeGroupIdsPlugin.Specification,
      permissions: ['HARTMANN_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    { specification: FilterConditionIgesPlugin.Specification },
    { specification: FilterConditionHybridDrgPlugin.Specification },
    { specification: FilterConditionPcclPlugin.Specification },
    {
      specification:
        FilterConditionAdministrativeDistrictIdPlugin.Specification,
    },
    { specification: FilterConditionAgmPlugin.Specification },
    { specification: FilterConditionCodeSpectrumPlugin.Specification },
    { specification: FilterConditionPostalCodesPlugin.Specification },
    { specification: FilterConditionStateIdPlugin.Specification },
    { specification: FilterConditionFabKeysPlugin.Specification },
    { specification: FilterConditionAdmissionCausePlugin.Specification },
    { specification: FilterConditionAdmissionDatePlugin.Specification },
    { specification: FilterConditionAdmissionReasonPlugin.Specification },
    { specification: FilterConditionDismissalDatePlugin.Specification },
    { specification: FilterConditionInlierTypesPlugin.Specification },
    { specification: FilterConditionInsuranceTypePlugin.Specification },
    { specification: FilterConditionGenderPlugin.Specification },
    { specification: FilterConditionAbteilungsartPlugin.Specification },
    {
      specification:
        FilterConditionArtificialRespirationHoursPlugin.Specification,
    },
    { specification: FilterConditionDismissionCausePlugin.Specification },
    { specification: FilterConditionNrwServiceAreaIdsPlugin.Specification },
    { specification: FilterConditionNrwServiceGroupIdsPlugin.Specification },
    { specification: FilterConditionKhtgLeistungsgruppePlugin.Specification },
    {
      specification:
        FilterConditionVirtualClinicSpecialtyDepartmentIdsPlugin.Specification,
    },
    { specification: FilterConditionOperatingNumberFilterPlugin.Specification },
    { specification: FilterConditionDrivingTimePlugin.Specification },
    { specification: FilterConditionChargeRealmPlugin.Specification },
    { specification: FilterConditionDrgCodeExistsPlugin.Specification },
    {
      specification:
        FilterConditionIntuitiveFileOfTruthCategoryPlugin.Specification,
      permissions: ['INTUITIVE_META_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    {
      specification:
        FilterConditionIntuitiveFileOfTruthProcedureNamePlugin.Specification,
      permissions: ['INTUITIVE_META_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    {
      specification:
        FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin.Specification,
      permissions: ['INTUITIVE_META_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    {
      specification:
        FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin.Specification,
      permissions: ['INTUITIVE_META_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    {
      specification:
        FilterConditionIntuitiveFileOfTruthConversionPlugin.Specification,
      permissions: ['INTUITIVE_META_VIRTUAL_CLINIC_ANALYSIS:BASE'],
    },
    {
      specification:
        FilterConditionOpNotfalleingriffPlugin.Specification,
      permissions: ['INTUITIVE_OP_MODULE:BASE'],
    },
    {
      specification: FilterConditionOpHasDataPlugin.Specification,
      permissions: ['INTUITIVE_OP_MODULE:BASE'],
    },
    {
      specification:
        FilterConditionOpDaVinciSystemIdPlugin.Specification,
      permissions: ['INTUITIVE_OP_MODULE:BASE'],
    },
    {
      specification: FilterConditionOpSaalPlugin.Specification,
      permissions: ['INTUITIVE_OP_MODULE:BASE'],
    },
  ];

  public static async fromObject(
    obj: {
      type: FilterConditionPluginFilterType;
      expression: FilterConditionExpression;
    },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<VirtualClinicAnalysisFullyInpatientCasesFilter> {
    return FilterConditionUtil.fromObject(
      obj.expression,
      registeredFilter,
    ).then(
      (expression) =>
        new VirtualClinicAnalysisFullyInpatientCasesFilter(expression),
    );
  }

  constructor(
    expression: FilterConditionExpression = FilterConditionUtil.EMPTY(),
  ) {
    super('fully-inpatient-cases-filter', expression);
  }

  public clone(): VirtualClinicAnalysisFullyInpatientCasesFilter {
    return new VirtualClinicAnalysisFullyInpatientCasesFilter(
      this.expression.clone(),
    );
  }

  public overrideClone(
    override: OverrideProperty[],
  ): VirtualClinicAnalysisFullyInpatientCasesFilter {
    return new VirtualClinicAnalysisFullyInpatientCasesFilter(
      this.expression.overrideClone(override),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by abstract base class
  public onUpdateVirtualClinicId(virtualClinicId: string): void {
    this.expression = FilterConditionUtil.withoutType(
      this.expression,
      FilterConditionVirtualClinicSpecialtyDepartmentIdsPluginType,
    );
  }
}
